import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axiosInstance from '@/utils/axiosInstance';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Search, ExternalLink, Trash2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import ConfirmDeleteModal from '@/components/ConfirmDeleteModal';

const IssueReportsActivity = () => {
  const { t } = useTranslation('admin');
  const { toast } = useToast();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ open: false, id: null });
  const [filters, setFilters] = useState({
    issue_type: '',
    is_active: '',
    brand: '',
    search: '',
    date_from: '',
    date_to: '',
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [pagination, setPagination] = useState({
    count: 0,
    page: 1,
    page_size: 20,
    total_pages: 1
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchData();
    }, 300);
    return () => clearTimeout(timer);
  }, [filters, currentPage, itemsPerPage]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      // Add pagination params
      params.append('page', currentPage.toString());
      params.append('page_size', itemsPerPage.toString());

      const response = await axiosInstance.get(`/api/admin/activity/issue-reports/?${params}`);
      setData(response.data.results || []);
      setPagination({
        count: response.data.count || 0,
        page: response.data.page || 1,
        page_size: response.data.page_size || itemsPerPage,
        total_pages: response.data.total_pages || 1
      });
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  const getIssueTypeBadge = (type) => {
    const colors = {
      bad_url: 'bg-[#3a405a] hover:bg-[#3a405a] text-white',
      wrong_rrp: 'bg-[#aec5eb] hover:bg-[#aec5eb] text-black',
      wrong_ean: 'bg-[#f9dec9] hover:bg-[#f9dec9] text-black',
      wrong_vendor_code: 'bg-[#e9afa3] hover:bg-[#e9afa3] text-black',
    };
    return (
      <Badge className={`${colors[type] || 'bg-gray-500 hover:bg-gray-500 text-white'} text-center`}>
        {t(`issueReports.types.${type}`) || type}
      </Badge>
    );
  };

  const getStatusBadge = (isActive) => {
    return isActive ? (
      <Badge variant="secondary">{t('issueReports.active')}</Badge>
    ) : (
      <Badge className="bg-green-500 hover:bg-green-500">{t('issueReports.resolved')}</Badge>
    );
  };

  const deleteReport = async () => {
    const reportId = deleteModal.id;
    if (!reportId) return;
    setUpdatingId(reportId);
    try {
      await axiosInstance.delete(`/api/admin/activity/issue-reports/${reportId}/delete/`);
      setData(prev => prev.filter(item => item.id !== reportId));
      toast({ description: t('common.deleted') });
      setDeleteModal({ open: false, id: null });
    } catch (error) {
      console.error('Error deleting report:', error);
      toast({ variant: 'destructive', description: t('common.deleteFailed') });
    } finally {
      setUpdatingId(null);
    }
  };

  const getProductRef = (item) => {
    if (item.shopify_id) return `Shopify ${item.shopify_id}`;
    if (item.product_ean) return `EAN ${item.product_ean}`;
    return '-';
  };

  const getIssueDetails = (item) => {
    switch (item.issue_type) {
      case 'bad_url':
        return item.reported_url ? (
          <a
            href={item.reported_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline flex items-center gap-1"
          >
            {item.reported_url.substring(0, 40)}...
            <ExternalLink className="h-3 w-3" />
          </a>
        ) : '-';
      case 'wrong_rrp':
        return `${item.current_rrp || '?'} → ${item.proposed_rrp || '?'}`;
      case 'wrong_ean':
        return `${item.current_ean || '?'} → ${item.proposed_ean || '?'}`;
      case 'wrong_vendor_code':
        return `${item.current_vendor_code || '?'} → ${item.proposed_vendor_code || '?'}`;
      default:
        return '-';
    }
  };

  // Use server-side pagination
  const totalPages = pagination.total_pages;

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-4">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t('activity.searchPlaceholder')}
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            className="pl-9"
          />
        </div>

        <Select
          value={filters.issue_type || 'all'}
          onValueChange={(v) => setFilters({ ...filters, issue_type: v === 'all' ? '' : v })}
        >
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder={t('issueReports.filterByType')}>
              {filters.issue_type ? getIssueTypeBadge(filters.issue_type) : t('issueReports.allTypes')}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('issueReports.allTypes')}</SelectItem>
            <SelectItem value="bad_url">
              <Badge className="bg-[#3a405a] hover:bg-[#3a405a] text-white text-center">{t('issueReports.types.bad_url')}</Badge>
            </SelectItem>
            <SelectItem value="wrong_rrp">
              <Badge className="bg-[#aec5eb] hover:bg-[#aec5eb] text-black text-center">{t('issueReports.types.wrong_rrp')}</Badge>
            </SelectItem>
            <SelectItem value="wrong_ean">
              <Badge className="bg-[#f9dec9] hover:bg-[#f9dec9] text-black text-center">{t('issueReports.types.wrong_ean')}</Badge>
            </SelectItem>
            <SelectItem value="wrong_vendor_code">
              <Badge className="bg-[#e9afa3] hover:bg-[#e9afa3] text-black text-center">{t('issueReports.types.wrong_vendor_code')}</Badge>
            </SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={filters.is_active || 'all'}
          onValueChange={(v) => setFilters({ ...filters, is_active: v === 'all' ? '' : v })}
        >
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder={t('issueReports.filterByStatus')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('issueReports.allStatuses')}</SelectItem>
            <SelectItem value="true">{t('issueReports.active')}</SelectItem>
            <SelectItem value="false">{t('issueReports.resolved')}</SelectItem>
          </SelectContent>
        </Select>

        <Input
          type="date"
          value={filters.date_from}
          onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
          className="w-[150px]"
        />

        <Input
          type="date"
          value={filters.date_to}
          onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
          className="w-[150px]"
        />

        <span className="text-sm text-muted-foreground self-center ml-auto text-right">
          {pagination.count} {t('activity.results')}
        </span>
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('issueReports.issueType')}</TableHead>
              <TableHead>{t('issueReports.brand')}</TableHead>
              <TableHead>{t('issueReports.product')}</TableHead>
              <TableHead className="w-[300px]">{t('issueReports.details')}</TableHead>
              <TableHead>{t('issueReports.status')}</TableHead>
              <TableHead>{t('activity.createdBy')}</TableHead>
              <TableHead>{t('activity.createdAt')}</TableHead>
              <TableHead className="w-[100px]">{t('common.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8">
                  {t('common.loading')}
                </TableCell>
              </TableRow>
            ) : data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                  {t('activity.noData')}
                </TableCell>
              </TableRow>
            ) : (
              data.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{getIssueTypeBadge(item.issue_type)}</TableCell>
                  <TableCell>{item.brand || '-'}</TableCell>
                  <TableCell className="font-mono text-sm">{getProductRef(item)}</TableCell>
                  <TableCell>{getIssueDetails(item)}</TableCell>
                  <TableCell>{getStatusBadge(item.is_active)}</TableCell>
                  <TableCell>{item.created_by || '-'}</TableCell>
                  <TableCell>{formatDate(item.created_at)}</TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDeleteModal({ open: true, id: item.id })}
                      disabled={updatingId === item.id}
                      title={t('common.delete')}
                    >
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {pagination.count > 0 && (
        <div className="flex items-center justify-between mt-4 px-2">
          <div className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">{t('pagination.rowsPerPage')}:</span>
            <select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="border rounded px-2 py-1 text-sm bg-background"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>

          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3"
            >
              {t('pagination.previous')}
            </Button>

            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }

              return (
                <Button
                  key={pageNum}
                  variant={currentPage === pageNum ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setCurrentPage(pageNum)}
                  className="w-9 h-9 p-0"
                >
                  {pageNum}
                </Button>
              );
            })}

            {totalPages > 5 && currentPage < totalPages - 2 && (
              <>
                <span className="px-2 text-muted-foreground">...</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setCurrentPage(totalPages)}
                  className="w-9 h-9 p-0"
                >
                  {totalPages}
                </Button>
              </>
            )}

            <Button
              variant="ghost"
              size="sm"
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages || totalPages === 0}
              className="px-3"
            >
              {t('pagination.next')}
            </Button>
          </div>
        </div>
      )}

      <ConfirmDeleteModal
        open={deleteModal.open}
        onOpenChange={(open) => setDeleteModal({ open, id: open ? deleteModal.id : null })}
        onConfirm={deleteReport}
        title={t('common.confirmDeleteTitle')}
        description={t('common.confirmDeleteDescription')}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        loading={updatingId === deleteModal.id}
      />
    </div>
  );
};

export default IssueReportsActivity;
